import { useGetPublishedPostsQuery, useGetPublicCategoriesQuery } from "../../../../../app/websiteApiSlice";
import { useGetPublicSchoolInfoQuery } from "../../../../../app/authApiSlice";
import WebsiteHeader from "../../WebsiteHeader";
import WebsiteFooter from "../../WebsiteFooter";
import SeoHead from "../../SeoHead";
import "./classic.css";
import {
  Box, Container, Typography, Grid, Divider, List, ListItemButton,
  ListItemText, Chip, CircularProgress, Button,
} from "@mui/material";
import { Link } from "react-router-dom";
import { format } from "date-fns";

export default function ClassicTheme({ settings }) {
  const { data: school } = useGetPublicSchoolInfoQuery();
  const { data: postsData, isLoading } = useGetPublishedPostsQuery({ per_page: 5 });
  const { data: categories = [] } = useGetPublicCategoriesQuery();

  return (
    <Box className="website-classic" sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#fdfaf5" }}>
      <SeoHead title={settings?.tagline || school?.name} description={settings?.tagline} schoolName={school?.name} />
      <WebsiteHeader school={school} navItems={settings?.nav_items} variant="classic" />

      {/* Masthead */}
      <Box sx={{ bgcolor: "#4e342e", color: "white", py: 5, textAlign: "center" }}>
        <Container maxWidth="md">
          {school?.logo && (
            <Box component="img" src={school.logo} alt={school.name}
              sx={{ height: 80, objectFit: "contain", mb: 2, filter: "brightness(0) invert(1)" }}
            />
          )}
          <Typography variant="h3" fontWeight={700} sx={{ fontFamily: "Georgia, serif" }}>
            {school?.name}
          </Typography>
          {settings?.tagline && (
            <Typography variant="subtitle1" sx={{ mt: 1, fontStyle: "italic", opacity: 0.85 }}>
              {settings.tagline}
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5, flex: 1 }}>
        <Grid container spacing={4}>
          {/* Main content */}
          <Grid item xs={12} md={8}>
            <Typography variant="h5" fontWeight={700} sx={{ fontFamily: "Georgia, serif", mb: 2 }}>
              Latest News
            </Typography>
            <Divider sx={{ borderColor: "#795548", borderWidth: 2, mb: 3 }} />

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : (
              (postsData?.posts || []).map((post, i) => (
                <Box key={post.id} mb={4}>
                  {post.featured_image && (
                    <Box component="img" src={post.featured_image} alt={post.title}
                      sx={{ width: "100%", maxHeight: 260, objectFit: "cover", mb: 2, border: "3px solid #e0d6c8" }}
                    />
                  )}
                  <Box display="flex" gap={0.5} mb={1}>
                    {post.categories?.map((c) => (
                      <Chip key={c.id} label={c.name} size="small" sx={{ bgcolor: "#795548", color: "white" }} />
                    ))}
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    component={Link}
                    to={`/blog/${post.slug}`}
                    sx={{ textDecoration: "none", color: "#3e2723", fontFamily: "Georgia, serif", "&:hover": { textDecoration: "underline" } }}
                  >
                    {post.title}
                  </Typography>
                  {post.published_at && (
                    <Typography variant="caption" display="block" color="text.secondary" mb={0.5}>
                      {format(new Date(post.published_at), "MMMM d, yyyy")}
                    </Typography>
                  )}
                  {post.excerpt && (
                    <Typography variant="body2" color="text.secondary">{post.excerpt}</Typography>
                  )}
                  {i < (postsData?.posts?.length ?? 0) - 1 && <Divider sx={{ mt: 3 }} />}
                </Box>
              ))
            )}

            <Button component={Link} to="/blog" variant="outlined" sx={{ borderColor: "#795548", color: "#795548" }}>
              View All Posts →
            </Button>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ bgcolor: "#fdf3e7", border: "1px solid #e0d6c8", borderRadius: 2, p: 2.5, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: "Georgia, serif", mb: 1.5 }}>
                Categories
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <List dense disablePadding>
                {categories.map((c) => (
                  <ListItemButton key={c.id} component={Link} to={`/blog/category/${c.slug}`} sx={{ borderRadius: 1 }}>
                    <ListItemText primary={c.name} />
                  </ListItemButton>
                ))}
                {categories.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No categories yet.</Typography>
                )}
              </List>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <WebsiteFooter settings={settings} school={school} variant="classic" />
    </Box>
  );
}
